import { useState, useCallback } from 'react';
import UserBioDialog from '../components/UserBioDialog';

/**
 * Hook to manage UserBioDialog state and provide easy methods to show user bios
 * 
 * Usage:
 * const { showUserBio, UserBioDialogComponent } = useUserBioDialog();
 * 
 * // Then in your component:
 * return (
 *   <>
 *     <button onClick={() => showUserBio('user123', 'John Doe', 'john@example.com')}>
 *       Show User Bio
 *     </button>
 *     {UserBioDialogComponent}
 *   </>
 * );
 */
export const useUserBioDialog = () => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    userId: null,
    userName: null,
    userEmail: null
  });

  const showUserBio = useCallback((userId, userName = null, userEmail = null) => {
    if (!userId) {
      console.warn('showUserBio: userId is required');
      return;
    }

    setDialogState({
      isOpen: true,
      userId,
      userName,
      userEmail
    });
  }, []);

  const hideUserBio = useCallback(() => {
    setDialogState({
      isOpen: false,
      userId: null,
      userName: null,
      userEmail: null
    });
  }, []);

  const UserBioDialogComponent = dialogState.isOpen ? (
    <UserBioDialog
      isOpen={dialogState.isOpen}
      onClose={hideUserBio}
      userId={dialogState.userId}
      userName={dialogState.userName}
      userEmail={dialogState.userEmail}
    />
  ) : null;

  return {
    showUserBio,
    hideUserBio,
    UserBioDialogComponent,
    isDialogOpen: dialogState.isOpen
  };
};

